import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  Optional,
  ViewChild,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { SelectButtonOptionClickEvent } from "src/app/shared/blocks/select-button/events/select-button-option-click-event";
import { SelectButtonItem } from "src/app/shared/blocks/select-button/models/select-button-item";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumMember,
  ForumThread,
  ForumThreadType,
} from "src/app/shared/interfaces/forum";
import { IconName } from "src/app/shared/types/icon-name";
import { IconSet } from "src/app/shared/types/icon-set";
import { ForumEditorComponent } from "../forum-editor/forum-editor.component";
import { SharedModule } from "src/app/shared/shared.module";

export type ForumCreateThreadModalResult = {
  title: string;
  content: string;
  type: ForumThreadType;
  send_announcement_email?: boolean;
  attachments?: File[];
  removedAttachmentIds?: string[];
};

export type ForumCreateThreadModalData = {
  mode?: "create" | "edit";
  thread?: ForumThread | null;
  isStudent?: boolean;
  members?: ForumMember[];
  attachments?: ForumAttachment[];
  attachmentConfig?: ForumAttachmentConfig | null;
};

const validateForumContent = (
  control: AbstractControl<string | null>,
): ValidationErrors | null => {
  const plainText = (control.value || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

  return plainText.length ? null : { required: true };
};

@Component({
  selector: "forum-create-thread-modal",
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    ForumEditorComponent,
  ],
  templateUrl: "./forum-create-thread-modal.component.html",
  styleUrls: ["./forum-create-thread-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForumCreateThreadModalComponent implements OnInit {
  @ViewChild(ForumEditorComponent) editorComponent?: ForumEditorComponent;
  existingAttachments: ForumAttachment[] = [];
  removedAttachmentIds = new Set<string>();

  readonly threadTypeOptions: Array<SelectButtonItem<ForumThreadType>> = [
    {
      label: "GROUPS.LIST.THREAD_TYPE_ANNOUNCEMENT",
      value: ForumThreadType.Announcement,
    },
    {
      label: "GROUPS.LIST.THREAD_TYPE_DISCUSSION",
      value: ForumThreadType.Discussion,
    },
  ];

  readonly form = new FormGroup({
    title: new FormControl("", [
      Validators.required,
      Validators.maxLength(255),
    ]),
    type: new FormControl<ForumThreadType | null>(null, [Validators.required]),
    content: new FormControl("", [validateForumContent]),
    send_announcement_email: new FormControl(false),
  });

  isEditMode = false;
  isStudentMode = false;
  ForumThreadType = ForumThreadType;

  constructor(
    private readonly dialogRef: MatDialogRef<
      ForumCreateThreadModalComponent,
      ForumCreateThreadModalResult
    >,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public readonly data: ForumCreateThreadModalData | null,
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data?.mode === "edit";
    this.isStudentMode = Boolean(this.data?.isStudent);

    if (!this.isEditMode && this.isStudentMode) {
      this.form.controls.type.setValue(ForumThreadType.Discussion);
    }

    if (!this.data?.thread) {
      return;
    }

    this.existingAttachments = [...(this.data.attachments || [])];

    this.form.patchValue({
      title: this.data.thread.title || "",
      type: this.data.thread.type ?? null,
      content: this.data.thread.content || "",
    });
  }

  get modalIconName(): IconName {
    return this.isEditMode ? "edit" : "plus";
  }

  get modalIconSet(): IconSet {
    return this.isEditMode ? "tabler" : "lucide";
  }

  get modalIconVariant(): "secondary" | "success" {
    return this.isEditMode ? "secondary" : "success";
  }

  get modalTitleKey(): string {
    return this.isEditMode
      ? "GROUPS.LIST.EDIT_THREAD_TITLE"
      : "GROUPS.LIST.CREATE_THREAD_TITLE";
  }

  get submitButtonKey(): string {
    return this.isEditMode
      ? "GROUPS.LIST.EDIT_THREAD_SUBMIT"
      : "GROUPS.LIST.CREATE_THREAD_SUBMIT";
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (this.form.controls.content.invalid) {
        this.editorComponent?.focus();
      }
      return;
    }

    this.dialogRef.close({
      title: this.form.value.title?.trim() || "",
      content: this.form.value.content || "",
      type: this.form.value.type,
      send_announcement_email:
        this.form.value.type == ForumThreadType.Discussion
          ? false
          : this.form.value.send_announcement_email,
      attachments: this.editorComponent?.getSelectedAttachments() || [],
      removedAttachmentIds: Array.from(this.removedAttachmentIds),
    });
  }

  selectThreadType(
    event: SelectButtonOptionClickEvent<ForumThreadType> | null,
  ): void {
    if (this.isEditMode) {
      return;
    }

    this.form.controls.type.setValue(event?.option?.value || null);
    this.form.controls.type.markAsTouched();
    this.form.controls.type.updateValueAndValidity();
  }

  close(): void {
    this.dialogRef.close();
  }

  removeExistingAttachment(index: number): void {
    const attachment = this.existingAttachments[index];

    if (!attachment) {
      return;
    }

    this.removedAttachmentIds.add(attachment.id);

    this.existingAttachments = this.existingAttachments.filter(
      (_, currentIndex) => currentIndex !== index,
    );
  }
}
